<script setup lang="ts">
  import type { Recipe, RecipeCategory } from 'types';
  import type { Ref } from 'vue';

  import useDisplay from '@/composables/useDisplay';
  import usePinned from '@/composables/usePinned';

  useHead({ title: 'home' });
  const router = useRouter();
  const { data: recipes, error, pending } = useRecipes();
  const { display, toggleDisplay } = useDisplay();
  const { pinned, isPinned, togglePinned } = usePinned();
  const search = inject<Ref<string>>('search');

  const categories = computed(() => {
    if (!recipes?.value)
      return {} as Record<RecipeCategory | 'pinned', Recipe[]>;
    return Object.fromEntries(
      Object.entries({
        pinned: filterRecipes(pinned.value, search?.value || ''),
        ...getCategories(recipes.value, search?.value || ''),
      }).filter((entry) => entry[1].length),
    );
  });

  function randomRecipe(category?: RecipeCategory | 'pinned') {
    const list = category
      ? categories.value[category]
      : Object.values(categories.value).flat();
    if (!list?.length) return;
    const index = Math.floor(Math.random() * list.length);
    const recipe = list[index];
    if (!recipe) return;
    return router.push(`/recipe/${recipe.slug}`);
  }
</script>

<template>
  <div class="page--home">
    <p v-if="pending">Loading...</p>
    <p
      v-else-if="
        error ||
        !recipes ||
        recipes.length === 0 ||
        Object.keys(categories).length === 0
      "
    >
      No recipes found. :(
    </p>
    <template v-else-if="categories">
      <TransitionGroup name="page">
        <section
          v-for="(items, category) in categories"
          :key="category"
          :class="`section section--${category as string}`"
        >
          <h2 class="section__heading">
            {{ category }}
            <button
              class="shuffle-button"
              @click="randomRecipe(category as RecipeCategory | 'pinned')"
            >
              <i class="fad fa-fw fa-shuffle" />
            </button>
          </h2>
          <Transition name="page" mode="out-in">
            <ul v-if="display === 'list'" class="section__recipeList">
              <TransitionGroup name="page">
                <li
                  v-for="recipe in items"
                  :key="recipe.slug as string"
                  class="section__recipeItem"
                  :class="{
                    'section__recipeItem--pinned': category === 'pinned',
                  }"
                >
                  <button
                    v-if="category === 'pinned'"
                    class="pin-button mr-2"
                    @click="togglePinned(recipe)"
                  >
                    <i
                      class="far fa-fw fa-thumbtack text-juss-purple-100 transition hover:text-juss-purple-300"
                      :class="{ 'fas text-juss-purple-400': isPinned(recipe) }"
                    />
                  </button>
                  <NuxtLink
                    :to="`/recipe/${recipe.slug}`"
                    prefetch
                    class="section__recipeLink"
                  >
                    {{ recipe.title }} {{ recipe.subtitle || '' }}
                  </NuxtLink>
                </li>
              </TransitionGroup>
            </ul>
            <div v-else-if="display === 'card'">
              <Slider :data="items" class="section__recipeSlider">
                <template #default="{ item: recipe }">
                  <NuxtLink :to="`/recipe/${recipe.slug}`" prefetch>
                    <RecipeCard
                      :recipe="recipe"
                      :is-pinned="isPinned(recipe)"
                      @toggle-pinned="togglePinned(recipe)"
                    />
                  </NuxtLink>
                </template>
              </Slider>
            </div>
          </Transition>
        </section>
      </TransitionGroup>
    </template>

    <button class="display-toggle" @click="toggleDisplay">
      <Transition name="page" mode="out-in">
        <i v-if="display === 'list'" class="fad fa-fw fa-cards-blank" />
        <i v-else class="fad fa-fw fa-list-ul" />
      </Transition>
    </button>
  </div>
</template>

<style lang="sass">
  .page--home
    .section + .section
      margin: 1.5em 0

    .section__heading
      font-size: 2.4em
      text-transform: lowercase
      margin-bottom: .2em
      position: relative
      z-index: 10

    .section__recipeList
      list-style: none
      padding: 0
      margin-left: .8em

    .section__recipeItem
      position: relative
      margin-left: 15px
      margin-bottom: .35em
      line-height: 1.3
      &::before
        content: '\F00D'
        position: absolute
        top: 0
        bottom: 0
        left: -18px
        display: flex
        align-items: center
        font-family: 'Font Awesome 6 Pro'
        font-size: 0.6em
        height: 2.75em
        color: #b4a5c3
      &--pinned
        margin-left: -12px
        &::before
          content: ''

    .section__recipeLink
      color: #544169
      text-decoration: none
      font-size: 1.1em

    .section__recipeSlider
      overflow: visible

    .section--error
      text-align: center
      font-weight: 700
      color: #baaeca

    .shuffle-button
      font-size: 20px
      line-height: 28px
      color: theme('colors.juss.purple.400')
      transition: 150ms ease-in-out
      &:hover
        color: theme('colors.juss.purple.600')

    .display-toggle
      position: fixed
      right: 24px
      bottom: 24px
      z-index: 10
      aspect-ratio: 1 / 1
      width: 40px
      border-radius: 4px
      background-color: theme('colors.slate.100')
      padding: 8px
      color: theme('colors.slate.400')
      transition: 150ms ease-in-out
      &:hover
        background-color: theme('colors.slate.200')
        color: theme('colors.purple.500')
</style>
